import { useEffect, useMemo, useState } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Flask, MagicWand, PencilSimple, Plus, Translate, Trash } from "@phosphor-icons/react";

const LEVELS = [
  { key: "curious", label: "Level 1 — Curious (kids/beginners)" },
  { key: "student", label: "Level 2 — Student (school)" },
  { key: "advanced", label: "Level 3 — Advanced (university)" },
  { key: "expert", label: "Level 4 — Expert (research context)" },
];
const EVIDENCE = ["well-established", "strong-evidence", "active-research", "uncertain", "speculative"];
const CONTENT_TYPES = ["concept", "explainer", "question", "experiment", "quiz", "study-guide", "glossary", "scientist", "discovery", "species", "element", "molecule", "space-object", "news", "science-of", "visual"];
const STATUSES = ["idea", "researching", "outline", "draft", "fact-check", "scientific-review", "editorial-review", "translation", "ready", "published", "needs-update", "archived"];

const emptyForm = {
  concept_id: "", pillar: "physics", subject: "", title: "", scientific_name: "",
  quick_answer: "", short_definition: "",
  levels: { curious: "", student: "", advanced: "", expert: "" },
  key_terms_text: "", prerequisites: "", related: "", misconceptions_text: "",
  evidence_label: "well-established", content_type: "concept",
  wikipedia_url: "", sources_text: "", status: "draft", published_at: "",
};

const linesToList = (s) => s.split("\n").map((x) => x.trim()).filter(Boolean);
const kvLinesToList = (s) =>
  linesToList(s).map((l) => {
    const [term, ...rest] = l.split(":");
    return { term: term.trim(), definition: rest.join(":").trim() };
  }).filter((k) => k.term && k.definition);
const listToLines = (arr) => (arr || []).join("\n");
const kvToLines = (arr) => (arr || []).map((k) => `${k.term}: ${k.definition}`).join("\n");

/** Science Pulse panel for the unified CMS — list + guided concept editor
 * with AI draft + AI translate helpers (all AI output lands as draft/unreviewed). */
export default function AdminCmsScience() {
  const [taxonomy, setTaxonomy] = useState(null);
  const [langs, setLangs] = useState([]);
  const [concepts, setConcepts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [editing, setEditing] = useState(null); // concept_id or null
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [aiBusy, setAiBusy] = useState("");

  const load = () => {
    setLoading(true);
    http.get("/science/admin/concepts", { params: statusFilter ? { status: statusFilter } : {} })
      .then(({ data }) => setConcepts(data.concepts || []))
      .catch(() => toast.error("Couldn't load concepts"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    http.get("/science/taxonomy").then(({ data }) => setTaxonomy(data)).catch(() => {});
    http.get("/translate/languages").then(({ data }) => setLangs((data.languages || []).filter((l) => l.code !== "en"))).catch(() => {});
  }, []);
  useEffect(load, [statusFilter]);

  const subjects = useMemo(
    () => (taxonomy?.pillars || []).find((p) => p.slug === form?.pillar)?.subjects || [],
    [taxonomy, form?.pillar],
  );

  const openNew = () => { setEditing(null); setForm({ ...emptyForm, levels: { curious: "", student: "", advanced: "", expert: "" } }); };
  const openEdit = async (c) => {
    const { data } = await http.get(`/science/admin/concepts/${c.concept_id}`);
    const d = data.concept;
    setEditing(d.concept_id);
    setForm({
      concept_id: d.concept_id,
      pillar: d.pillar, subject: d.subject || "", title: d.title || "",
      scientific_name: d.scientific_name || "",
      quick_answer: d.quick_answer || "", short_definition: d.short_definition || "",
      levels: { curious: "", student: "", advanced: "", expert: "", ...(d.levels || {}) },
      key_terms_text: kvToLines(d.key_terms),
      prerequisites: (d.prerequisites || []).join(", "),
      related: (d.related || []).join(", "),
      misconceptions_text: listToLines(d.misconceptions),
      evidence_label: d.evidence_label || "well-established",
      content_type: d.content_type || "concept",
      wikipedia_url: d.wikipedia_url || "",
      sources_text: listToLines(d.sources),
      status: d.status || "draft",
      published_at: (d.published_at || "").slice(0, 10),
      language_versions: d.language_versions || {},
      ai_drafted: d.ai_drafted,
    });
  };

  const save = async () => {
    if (form.title.trim().length < 3) return toast.error("Title too short");
    setSaving(true);
    const payload = {
      pillar: form.pillar,
      subject: form.subject || null,
      title: form.title.trim(),
      scientific_name: form.scientific_name || null,
      quick_answer: form.quick_answer || null,
      short_definition: form.short_definition || null,
      levels: Object.fromEntries(Object.entries(form.levels).filter(([, v]) => v && v.trim())),
      key_terms: kvLinesToList(form.key_terms_text),
      prerequisites: form.prerequisites.split(",").map((s) => s.trim()).filter(Boolean),
      related: form.related.split(",").map((s) => s.trim()).filter(Boolean),
      misconceptions: linesToList(form.misconceptions_text),
      evidence_label: form.evidence_label,
      content_type: form.content_type,
      wikipedia_url: form.wikipedia_url || null,
      sources: linesToList(form.sources_text),
      status: form.status,
      published_at: form.published_at ? `${form.published_at}T09:00:00+00:00` : null,
    };
    if (!form.published_at) delete payload.published_at; // omit → server keeps existing date
    try {
      if (editing) {
        await http.patch(`/science/concepts/${editing}`, payload);
        toast.success("Concept updated");
      } else {
        const { data } = await http.post("/science/concepts", { ...payload, concept_id: form.concept_id || undefined });
        toast.success(`Concept '${data.concept.concept_id}' created`);
      }
      setForm(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const aiDraft = async () => {
    if (!form.title.trim()) return toast.error("Enter a topic/title first");
    setAiBusy("draft");
    try {
      const { data } = await http.post("/science/ai/draft", {
        topic: form.title.trim(), pillar: form.pillar, subject: form.subject || undefined,
        content_type: form.content_type,
      });
      toast.success(`AI draft '${data.concept.concept_id}' saved as DRAFT — fact-check before publishing.`);
      setForm(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI draft failed (is the LLM key active?)");
    } finally { setAiBusy(""); }
  };

  const aiTranslate = async (lang) => {
    setAiBusy(lang);
    try {
      const { data } = await http.post(`/science/concepts/${editing}/ai-translate`, { lang });
      toast.success(`${lang} version stored (unreviewed)`);
      setForm((f) => ({ ...f, language_versions: { ...(f.language_versions || {}), [lang]: data.version } }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI translation failed");
    } finally { setAiBusy(""); }
  };

  // ------------------------- editor -------------------------
  if (form) {
    return (
      <div data-testid="science-concept-editor">
        <button onClick={() => setForm(null)} data-testid="science-editor-back" className="inline-flex items-center gap-1 font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830] hover:text-[#1B4332]">
          <ArrowLeft size={12} weight="bold" /> Back to concepts
        </button>
        {form.ai_drafted && (
          <div className="mt-3 rounded-xl border border-violet-300 bg-violet-50 px-3 py-2 text-xs text-violet-800" data-testid="science-ai-banner">
            This concept was AI-drafted. Fact-check every claim, fix terminology, add sources — then move it through review before publishing.
          </div>
        )}

        <div className="mt-3 grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
          <div className="min-w-0 space-y-3">
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Concept title — e.g. What Is Entropy?"
              data-testid="science-editor-title"
              className="w-full rounded-xl border border-black/15 bg-white px-4 py-3 font-serif-display text-2xl" />
            <input value={form.short_definition} onChange={(e) => setForm({ ...form, short_definition: e.target.value })}
              placeholder="Short definition (one sentence)"
              data-testid="science-editor-definition"
              className="w-full rounded-xl border border-black/15 bg-white px-4 py-2.5 text-sm" />
            <textarea value={form.quick_answer} onChange={(e) => setForm({ ...form, quick_answer: e.target.value })}
              rows={2} placeholder="Quick answer — the 30-second version"
              data-testid="science-editor-quick-answer"
              className="w-full rounded-xl border border-black/15 bg-white px-4 py-2.5 text-sm" />

            <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)] pt-2">Explanation levels</div>
            {LEVELS.map((l) => (
              <div key={l.key}>
                <label className="block text-[10px] font-mono-tech tracking-[0.2em] uppercase text-[#3A3830] mb-1">{l.label}</label>
                <textarea
                  value={form.levels[l.key] || ""}
                  onChange={(e) => setForm({ ...form, levels: { ...form.levels, [l.key]: e.target.value } })}
                  rows={3}
                  data-testid={`science-editor-level-${l.key}`}
                  className="w-full rounded-xl border border-black/15 bg-white px-4 py-2.5 text-sm leading-relaxed"
                />
              </div>
            ))}

            <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)] pt-2">Knowledge graph</div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[10px] font-mono-tech tracking-[0.2em] uppercase text-[#3A3830] mb-1">Prerequisites (ids, comma-sep)</label>
                <input value={form.prerequisites} onChange={(e) => setForm({ ...form, prerequisites: e.target.value })}
                  placeholder="cells, sun" data-testid="science-editor-prereq"
                  className="w-full rounded-lg border border-black/15 bg-white px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-[10px] font-mono-tech tracking-[0.2em] uppercase text-[#3A3830] mb-1">Related concepts</label>
                <input value={form.related} onChange={(e) => setForm({ ...form, related: e.target.value })}
                  placeholder="water-cycle, carbon-cycle" data-testid="science-editor-related"
                  className="w-full rounded-lg border border-black/15 bg-white px-3 py-2 text-sm" />
              </div>
            </div>
            <div>
              <label className="block text-[10px] font-mono-tech tracking-[0.2em] uppercase text-[#3A3830] mb-1">Key terms (one per line — Term: definition)</label>
              <textarea value={form.key_terms_text} onChange={(e) => setForm({ ...form, key_terms_text: e.target.value })}
                rows={3} placeholder={"Chlorophyll: The green pigment that captures light.\nChloroplast: Where photosynthesis happens."}
                data-testid="science-editor-key-terms"
                className="w-full rounded-lg border border-black/15 bg-white px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-[10px] font-mono-tech tracking-[0.2em] uppercase text-[#3A3830] mb-1">Common misconceptions (one per line)</label>
              <textarea value={form.misconceptions_text} onChange={(e) => setForm({ ...form, misconceptions_text: e.target.value })}
                rows={2} data-testid="science-editor-misconceptions"
                className="w-full rounded-lg border border-black/15 bg-white px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-[10px] font-mono-tech tracking-[0.2em] uppercase text-[#3A3830] mb-1">Sources (one per line — institutions/textbooks)</label>
              <textarea value={form.sources_text} onChange={(e) => setForm({ ...form, sources_text: e.target.value })}
                rows={2} placeholder={"NASA\nOpenStax Biology 2e"} data-testid="science-editor-sources"
                className="w-full rounded-lg border border-black/15 bg-white px-3 py-2 text-sm" />
            </div>
          </div>

          <aside className="space-y-4">
            <div className="rounded-2xl border border-black/10 bg-white p-4">
              <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)] mb-2">Publish</div>
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
                data-testid="science-editor-status" className="w-full rounded-lg border border-black/15 bg-white px-3 py-2 text-sm">
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <button onClick={save} disabled={saving} data-testid="science-editor-save"
                className="mt-3 w-full rounded-full bg-[color:var(--np-brand,#B85F2A)] text-white px-4 py-2.5 font-mono-tech text-[10px] tracking-[0.28em] uppercase disabled:opacity-60">
                {saving ? "Saving…" : editing ? "Save changes" : "Create concept"}
              </button>
              {!editing && (
                <button onClick={aiDraft} disabled={aiBusy === "draft"} data-testid="science-ai-draft"
                  className="mt-2 w-full inline-flex items-center justify-center gap-1.5 rounded-full border border-violet-300 bg-violet-50 text-violet-800 px-4 py-2.5 font-mono-tech text-[10px] tracking-[0.24em] uppercase disabled:opacity-60">
                  <MagicWand size={13} weight="fill" /> {aiBusy === "draft" ? "Drafting…" : "AI draft this topic"}
                </button>
              )}
            </div>

            <div className="rounded-2xl border border-black/10 bg-white p-4">
              <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)] mb-2">Placement</div>
              <label className="block text-[10px] font-mono-tech tracking-[0.2em] uppercase text-[#3A3830] mb-1">Pillar</label>
              <select value={form.pillar} onChange={(e) => setForm({ ...form, pillar: e.target.value, subject: "" })}
                data-testid="science-editor-pillar" className="w-full rounded-lg border border-black/15 bg-white px-3 py-2 text-sm mb-2">
                {(taxonomy?.pillars || []).map((p) => <option key={p.slug} value={p.slug}>{p.emoji} {p.label}</option>)}
              </select>
              <label className="block text-[10px] font-mono-tech tracking-[0.2em] uppercase text-[#3A3830] mb-1">Subject</label>
              <select value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })}
                data-testid="science-editor-subject" className="w-full rounded-lg border border-black/15 bg-white px-3 py-2 text-sm mb-2">
                <option value="">—</option>
                {subjects.map((s) => <option key={s.slug} value={s.slug}>{s.label}</option>)}
              </select>
              <label className="block text-[10px] font-mono-tech tracking-[0.2em] uppercase text-[#3A3830] mb-1">Content type</label>
              <select value={form.content_type} onChange={(e) => setForm({ ...form, content_type: e.target.value })}
                data-testid="science-editor-type" className="w-full rounded-lg border border-black/15 bg-white px-3 py-2 text-sm mb-2">
                {CONTENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              {form.content_type === "news" && (
                <>
                  <label className="block text-[10px] font-mono-tech tracking-[0.2em] uppercase text-[#3A3830] mb-1">News date (Science Today)</label>
                  <input type="date" value={form.published_at} onChange={(e) => setForm({ ...form, published_at: e.target.value })}
                    data-testid="science-editor-pubdate" className="w-full rounded-lg border border-black/15 bg-white px-3 py-2 text-sm mb-2" />
                  <p className="text-[10px] text-[#3A3830]/60 -mt-1 mb-2">Published news items appear on /science/today, newest first.</p>
                </>
              )}
              <label className="block text-[10px] font-mono-tech tracking-[0.2em] uppercase text-[#3A3830] mb-1">Evidence label</label>
              <select value={form.evidence_label} onChange={(e) => setForm({ ...form, evidence_label: e.target.value })}
                data-testid="science-editor-evidence" className="w-full rounded-lg border border-black/15 bg-white px-3 py-2 text-sm mb-2">
                {EVIDENCE.map((t) => <option key={t} value={t}>{t.replace("-", " ")}</option>)}
              </select>
              <label className="block text-[10px] font-mono-tech tracking-[0.2em] uppercase text-[#3A3830] mb-1">Scientific name (optional)</label>
              <input value={form.scientific_name} onChange={(e) => setForm({ ...form, scientific_name: e.target.value })}
                data-testid="science-editor-sciname" className="w-full rounded-lg border border-black/15 bg-white px-3 py-2 text-sm mb-2 italic" />
              <label className="block text-[10px] font-mono-tech tracking-[0.2em] uppercase text-[#3A3830] mb-1">Wikipedia URL</label>
              <input value={form.wikipedia_url} onChange={(e) => setForm({ ...form, wikipedia_url: e.target.value })}
                placeholder="https://en.wikipedia.org/wiki/…" data-testid="science-editor-wiki"
                className="w-full rounded-lg border border-black/15 bg-white px-3 py-2 text-sm" />
            </div>

            {editing && (
              <div className="rounded-2xl border border-black/10 bg-white p-4">
                <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)] mb-2">
                  <Translate size={12} weight="fill" className="inline mr-1" /> Language versions
                </div>
                <div className="flex flex-wrap gap-1 mb-2">
                  {Object.keys(form.language_versions || {}).map((l) => (
                    <span key={l} className="rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 px-2 py-0.5 font-mono-tech text-[9px] uppercase tracking-[0.18em]" data-testid={`science-lang-chip-${l}`}>
                      {l}{(form.language_versions[l] || {}).reviewed ? " ✓" : " (unreviewed)"}
                    </span>
                  ))}
                  {Object.keys(form.language_versions || {}).length === 0 && (
                    <span className="text-xs text-[#3A3830]/60">No translations yet.</span>
                  )}
                </div>
                <div className="flex gap-1.5">
                  <select id="science-ai-lang" data-testid="science-ai-lang" className="flex-1 rounded-lg border border-black/15 bg-white px-2 py-1.5 text-xs">
                    {langs.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
                  </select>
                  <button
                    onClick={() => aiTranslate(document.getElementById("science-ai-lang").value)}
                    disabled={!!aiBusy}
                    data-testid="science-ai-translate"
                    className="rounded-full border border-violet-300 bg-violet-50 text-violet-800 px-3 py-1.5 font-mono-tech text-[9px] tracking-[0.2em] uppercase disabled:opacity-60"
                  >{aiBusy && aiBusy !== "draft" ? "…" : "AI translate"}</button>
                </div>
              </div>
            )}
          </aside>
        </div>
      </div>
    );
  }

  // ------------------------- list -------------------------
  return (
    <div data-testid="science-panel">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-2">
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="science-status-filter"
            className="rounded-lg border border-black/15 bg-white px-3 py-2 text-sm">
            <option value="">All statuses</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <span className="font-mono-tech text-[9px] tracking-[0.22em] uppercase text-[#3A3830]">{concepts.length} concepts</span>
        </div>
        <button onClick={openNew} data-testid="science-new"
          className="inline-flex items-center gap-1.5 rounded-full bg-[color:var(--np-brand,#B85F2A)] text-white px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase">
          <Plus size={13} weight="bold" /> New concept
        </button>
      </div>
      {loading ? (
        <div className="font-mono-tech text-[10px] tracking-[0.3em] uppercase">Loading…</div>
      ) : (
        <ul className="space-y-2">
          {concepts.map((c) => (
            <li key={c.concept_id} data-testid={`science-row-${c.concept_id}`}
              className="rounded-xl border border-black/10 bg-white p-3.5 flex items-center gap-3 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="font-serif-display text-lg leading-tight">{c.title}</div>
                <div className="font-mono-tech text-[9px] tracking-[0.18em] uppercase text-[#3A3830]/70">
                  /{c.pillar}{c.subject ? `/${c.subject}` : ""} · {c.status} · {String(c.evidence_label || "").replace("-", " ")}
                  {c.ai_drafted && " · AI draft"}
                </div>
              </div>
              <a href={`/science/concept/${c.concept_id}`} target="_blank" rel="noopener noreferrer"
                className="rounded-full border border-black/15 bg-white px-3 py-1.5 font-mono-tech text-[9px] tracking-[0.24em] uppercase hover:bg-black/5">View</a>
              <button onClick={() => openEdit(c)} data-testid={`science-edit-${c.concept_id}`}
                className="rounded-full border border-black/15 bg-white px-3 py-1.5 font-mono-tech text-[9px] tracking-[0.24em] uppercase hover:bg-black/5">
                <PencilSimple size={12} weight="bold" /> Edit
              </button>
            </li>
          ))}
          {concepts.length === 0 && <li className="text-sm text-[#3A3830]">No concepts with this filter.</li>}
        </ul>
      )}
    </div>
  );
}
